import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import {
  createAutosaveSnapshot,
  hasMeaningfulAutosaveChanges,
  type NoteAutosaveSnapshot,
} from '../utils/noteAutosaveSnapshot';

export type AutosaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';

export type SaveIfChangedReason = 'autosave' | 'sign' | 'finish' | 'leave';

export interface SaveIfChangedResult {
  saved: boolean;
  response: QuestionnaireResponse | null;
  skipped: boolean;
}

export const PROVIDER_NOTE_AUTOSAVE_DEBOUNCE_MS = 2000;

/** Hard cap on consecutive sign/finish/leave drain saves — prevents UI lockup if snapshots never converge. */
const MAX_DRAIN_ATTEMPTS = 5;

export interface UseProviderNoteAutosaveOptions {
  enabled: boolean;
  debounceMs?: number;
  getCurrentSnapshot: () => NoteAutosaveSnapshot | null;
  /** Returns null when save did not occur (validation block or API failure). */
  persistSave: (
    metadataOverride?: NoteMetadataSnapshot,
    reason?: SaveIfChangedReason,
  ) => Promise<QuestionnaireResponse | null>;
  onSaved?: (response: QuestionnaireResponse, reason: SaveIfChangedReason) => void;
  runPostSaveChecks?: (metadata: NoteMetadataSnapshot | null, requestId: number) => void;
  checkGenerationRef?: React.MutableRefObject<number>;
}

export interface UseProviderNoteAutosaveResult {
  status: AutosaveStatus;
  initSnapshot: (snapshot: NoteAutosaveSnapshot | null) => void;
  scheduleAutosave: () => void;
  saveIfChanged: (
    reason: SaveIfChangedReason,
    metadataOverride?: NoteMetadataSnapshot,
  ) => Promise<SaveIfChangedResult>;
  flushDebounced: () => void;
  /** Flush debounced edits and persist pending changes before closing the editor or navigating away. */
  flushAutosaveBeforeLeave: () => Promise<SaveIfChangedResult>;
  isSaving: boolean;
}

export function buildSubmittedAutosaveSnapshot(
  current: NoteAutosaveSnapshot,
  metadataOverride?: NoteMetadataSnapshot,
): NoteAutosaveSnapshot {
  return createAutosaveSnapshot({
    ...current,
    ...(metadataOverride !== undefined ? { metadata: metadataOverride } : {}),
  });
}

export function saveReasonPriority(
  a: SaveIfChangedReason,
  b: SaveIfChangedReason,
): SaveIfChangedReason {
  const rank: Record<SaveIfChangedReason, number> = {
    sign: 4,
    finish: 3,
    leave: 2,
    autosave: 1,
  };
  return rank[a] >= rank[b] ? a : b;
}

function needsSyncDrainReason(reason: SaveIfChangedReason): boolean {
  return reason === 'sign' || reason === 'finish' || reason === 'leave';
}

export function useProviderNoteAutosave(
  options: UseProviderNoteAutosaveOptions,
): UseProviderNoteAutosaveResult {
  const {
    enabled,
    debounceMs = PROVIDER_NOTE_AUTOSAVE_DEBOUNCE_MS,
    getCurrentSnapshot,
    persistSave,
    onSaved,
    runPostSaveChecks,
    checkGenerationRef: externalCheckGenerationRef,
  } = options;

  const [status, setStatus] = useState<AutosaveStatus>('saved');
  const lastSavedSnapshotRef = useRef<NoteAutosaveSnapshot | null>(null);
  const initializedRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveInFlightRef = useRef<Promise<SaveIfChangedResult> | null>(null);
  const pendingAfterInFlightRef = useRef(false);
  const pendingFollowUpReasonRef = useRef<SaveIfChangedReason | null>(null);
  /** Prevents saveIfChanged → drain → saveIfChanged → drain exponential nesting. */
  const drainingRef = useRef(false);
  const internalCheckGenerationRef = useRef(0);
  const checkGenerationRef = externalCheckGenerationRef ?? internalCheckGenerationRef;
  const saveIfChangedRef = useRef<
    (reason: SaveIfChangedReason, metadataOverride?: NoteMetadataSnapshot) => Promise<SaveIfChangedResult>
  >(async () => ({ saved: false, response: null, skipped: true }));

  const initSnapshot = useCallback((snapshot: NoteAutosaveSnapshot | null) => {
    lastSavedSnapshotRef.current = snapshot ? createAutosaveSnapshot(snapshot) : null;
    initializedRef.current = snapshot != null;
    setStatus('saved');
  }, []);

  const drainUntilSynced = useCallback(
    async (
      reason: SaveIfChangedReason,
      metadataOverride?: NoteMetadataSnapshot,
      initialWait?: Promise<SaveIfChangedResult>,
    ): Promise<SaveIfChangedResult> => {
      let lastResult: SaveIfChangedResult = initialWait
        ? await initialWait
        : { saved: false, response: null, skipped: true };

      // Bounded: if the live snapshot never converges with the saved baseline
      // (e.g. a normalization mismatch), bail out instead of saving forever.
      for (let attempt = 0; attempt < MAX_DRAIN_ATTEMPTS; attempt += 1) {
        if (saveInFlightRef.current) {
          lastResult = await saveInFlightRef.current;
        }

        const current = getCurrentSnapshot();
        const lastSaved = lastSavedSnapshotRef.current;
        if (!current || !lastSaved || !hasMeaningfulAutosaveChanges(current, lastSaved)) {
          return lastResult;
        }

        lastResult = await saveIfChangedRef.current(reason, metadataOverride);
        if (!lastResult.saved && !lastResult.skipped) {
          return lastResult;
        }
      }

      console.warn(
        `Note autosave did not converge after ${MAX_DRAIN_ATTEMPTS} attempts (reason: ${reason}); stopping drain loop.`,
      );
      // Force baseline to the latest live snapshot so the footer leaves "Saving..."
      // and a later sign attempt does not immediately re-enter an endless drain.
      const finalSnapshot = getCurrentSnapshot();
      if (finalSnapshot) {
        lastSavedSnapshotRef.current = createAutosaveSnapshot(finalSnapshot);
      }
      setStatus('saved');
      return lastResult;
    },
    [getCurrentSnapshot],
  );

  const saveIfChanged = useCallback(
    async (
      reason: SaveIfChangedReason,
      metadataOverride?: NoteMetadataSnapshot,
    ): Promise<SaveIfChangedResult> => {
      if (saveInFlightRef.current) {
        pendingAfterInFlightRef.current = true;
        pendingFollowUpReasonRef.current = pendingFollowUpReasonRef.current
          ? saveReasonPriority(pendingFollowUpReasonRef.current, reason)
          : reason;

        if (needsSyncDrainReason(reason) && !drainingRef.current) {
          return drainUntilSynced(reason, metadataOverride, saveInFlightRef.current);
        }
        return saveInFlightRef.current;
      }

      const current = getCurrentSnapshot();
      if (!current || !initializedRef.current) {
        return { saved: false, response: null, skipped: true };
      }

      const lastSaved = lastSavedSnapshotRef.current;
      if (lastSaved && !hasMeaningfulAutosaveChanges(current, lastSaved)) {
        setStatus('saved');
        return { saved: false, response: null, skipped: true };
      }

      const submittedSnapshot = buildSubmittedAutosaveSnapshot(current, metadataOverride);
      setStatus('saving');

      // Persist only inside savePromise. Drain/follow-up runs AFTER the in-flight ref is
      // cleared and the promise resolves — otherwise drainUntilSynced can await the same
      // promise it is nested in and hang forever (Continue → Sign spinner).
      const savePromise = (async (): Promise<SaveIfChangedResult> => {
        try {
          const response = await persistSave(metadataOverride, reason);
          if (!response) {
            setStatus('error');
            return { saved: false, response: null, skipped: false };
          }

          onSaved?.(response, reason);
          lastSavedSnapshotRef.current = submittedSnapshot;
          setStatus('saved');

          const requestId = ++checkGenerationRef.current;
          runPostSaveChecks?.(metadataOverride ?? current.metadata, requestId);

          return { saved: true, response, skipped: false };
        } catch {
          setStatus('error');
          return { saved: false, response: null, skipped: false };
        } finally {
          saveInFlightRef.current = null;
        }
      })();

      saveInFlightRef.current = savePromise;
      const result = await savePromise;

      const followUpReason = pendingAfterInFlightRef.current
        ? (pendingFollowUpReasonRef.current ?? 'autosave')
        : null;
      pendingAfterInFlightRef.current = false;
      pendingFollowUpReasonRef.current = null;

      const fresh = getCurrentSnapshot();
      const savedBaseline = lastSavedSnapshotRef.current;
      const needsFollowUp =
        Boolean(fresh && savedBaseline && hasMeaningfulAutosaveChanges(fresh, savedBaseline));

      // Only the outermost sign/finish/leave call drains. Nested saves from drain must not
      // start another drain (that nested exponentially and left the footer on "Saving...").
      if (needsSyncDrainReason(reason) && result.saved && !drainingRef.current) {
        drainingRef.current = true;
        try {
          return await drainUntilSynced(reason, metadataOverride, Promise.resolve(result));
        } finally {
          drainingRef.current = false;
        }
      }

      if (!drainingRef.current && (needsFollowUp || followUpReason)) {
        void saveIfChangedRef.current(followUpReason ?? 'autosave');
      }

      return result;
    },
    [getCurrentSnapshot, persistSave, onSaved, runPostSaveChecks, drainUntilSynced, checkGenerationRef],
  );

  saveIfChangedRef.current = saveIfChanged;

  const scheduleAutosave = useCallback(() => {
    if (!enabled || !initializedRef.current) return;

    // Defer snapshot diffing to saveIfChanged — avoid deep-equal on every keystroke.
    setStatus((prev) => (prev === 'saving' ? prev : 'unsaved'));
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void saveIfChanged('autosave');
    }, debounceMs);
  }, [enabled, debounceMs, saveIfChanged]);

  const flushDebounced = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  const flushAutosaveBeforeLeave = useCallback(async (): Promise<SaveIfChangedResult> => {
    flushDebounced();
    if (saveInFlightRef.current) {
      // This is the outermost drain. Claim the guard so the saves it triggers
      // cannot each start their own drain — that nesting multiplied attempts and
      // left Save / Save and Sign spinning indefinitely.
      if (drainingRef.current) return saveInFlightRef.current;
      drainingRef.current = true;
      try {
        return await drainUntilSynced('leave', undefined, saveInFlightRef.current);
      } finally {
        drainingRef.current = false;
      }
    }
    return saveIfChanged('leave');
  }, [flushDebounced, drainUntilSynced, saveIfChanged]);

  return {
    status,
    initSnapshot,
    scheduleAutosave,
    saveIfChanged,
    flushDebounced,
    flushAutosaveBeforeLeave,
    isSaving: status === 'saving',
  };
}
